import { SubscribedPlayerSearchResponse } from "../../../../../interfaces";
import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Input,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "semantic-ui-react";
import "./styles.scss";
import { AbbreviatedPopover } from "../../../../../components";
import { fullNameFrom } from "../../../../../utils";

interface OwnProps {
  open: boolean;
  players: SubscribedPlayerSearchResponse[];
  values: number[];
  onUpdate: (values: number[]) => void;
  onClose: () => void;
}

type Props = OwnProps;

const PlayerSelectionModalFC: React.FC<Props> = ({ open, onClose, values, players, onUpdate }) => {
  const [selectedIds, setSelectedIds] = useState<number[]>(values);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    setSelectedIds(values);
  }, [values]);

  const filteredPlayers = useMemo(() => {
    if (searchTerm) {
      return players.filter(player => {
        const values = [
          player.playerAccount.firstName,
          player.playerAccount.lastName,
          player.playerAccount.email,
          ...(player.regionNames || []),
          ...(player.branchNames || []),
          ...(player.stateCodes || []),
        ];
        return values.some(v => v?.toLowerCase()?.includes(searchTerm.toLowerCase()));
      });
    } else return players;
  }, [searchTerm, players]);

  const selectAll = (selected: boolean) => {
    if (selected) {
      setSelectedIds(players.map(o => o.playerAccount.playerId!));
    } else {
      setSelectedIds([]);
    }
  };

  return (
    <Modal open={open} onClose={onClose} className={"playerSelectionModal"}>
      <Modal.Header>Select Specific Players</Modal.Header>
      <Modal.Content>
        <Input
          placeholder="Search..."
          value={searchTerm}
          className={"search"}
          onChange={(_, data) => setSearchTerm(data.value)}
        />
        <div className={"tableContainer"}>
          <Table compact>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>
                  <Checkbox
                    checked={selectedIds.length > 0 && selectedIds.length == players.length}
                    onChange={(_, data) => {
                      selectAll(!!data.checked);
                    }}
                  />
                </TableHeaderCell>
                <TableHeaderCell>Name</TableHeaderCell>
                <TableHeaderCell>Email</TableHeaderCell>
                <TableHeaderCell>Region</TableHeaderCell>
                <TableHeaderCell>Branch</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPlayers.map((player, index) => {
                const playerId = player.playerAccount.playerId!;
                return (
                  <TableRow key={`select-player-row-${index}`}>
                    <TableCell collapsing>
                      <Checkbox
                        checked={selectedIds.includes(playerId)}
                        onChange={(_, data) => {
                          if (!data.checked) setSelectedIds(s => s.filter(o => o != playerId));
                          else if (!selectedIds.includes(playerId)) setSelectedIds(s => [...s, playerId]);
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <AbbreviatedPopover text={fullNameFrom(player.playerAccount)} />
                    </TableCell>
                    <TableCell>
                      <AbbreviatedPopover text={player.playerAccount.email} maxLength={50} />
                    </TableCell>
                    <TableCell>
                      <AbbreviatedPopover text={player.regionNames?.join(", ")} />
                    </TableCell>
                    <TableCell>
                      <AbbreviatedPopover text={player.branchNames?.join(", ")} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Modal.Content>
      <Modal.Actions>
        <Button type="button" onClick={onClose} secondary>
          Close
        </Button>

        <Button type="button" onClick={() => onUpdate(selectedIds)} primary>
          Done
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export const PlayerSelectionModal = PlayerSelectionModalFC;
